import {
	useCallback,
	useDebugValue,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
	useSyncExternalStore,
} from "react";

type MatchMedia = (query: string) => MediaQueryList;
type SsrMatchMedia = (query: string) => { matches: boolean };

export type UseMediaQueryOptions = {
	defaultMatches?: boolean;
	matchMedia?: MatchMedia | null;
	ssrMatchMedia?: SsrMatchMedia | null;
	noSsr?: boolean;
};

const useEnhancedEffect =
	typeof window !== "undefined" ? useLayoutEffect : useEffect;

function useMediaQueryOld(
	query: string,
	defaultMatches: boolean,
	matchMedia: MatchMedia | null,
	ssrMatchMedia: SsrMatchMedia | null,
	noSsr: boolean,
) {
	const [match, setMatch] = useState(() => {
		if (noSsr && matchMedia) {
			return matchMedia(query).matches;
		}
		if (ssrMatchMedia) {
			return ssrMatchMedia(query).matches;
		}

		// Once mounted, listeners update to the real value.
		return defaultMatches;
	});

	useEnhancedEffect(() => {
		if (!matchMedia) {
			return undefined;
		}

		const queryList = matchMedia(query);
		const updateMatch = () => {
			setMatch(queryList.matches);
		};

		updateMatch();
		queryList.addEventListener("change", updateMatch);
		return () => {
			queryList.removeEventListener("change", updateMatch);
		};
	}, [query, matchMedia]);

	return match;
}

const maybeReactUseSyncExternalStore = { useSyncExternalStore }
	.useSyncExternalStore;

function useMediaQueryNew(
	query: string,
	defaultMatches: boolean,
	matchMedia: MatchMedia | null,
	ssrMatchMedia: SsrMatchMedia | null,
	noSsr: boolean,
) {
	const getDefaultSnapshot = useCallback(
		() => defaultMatches,
		[defaultMatches],
	);

	const getServerSnapshot = useMemo(() => {
		if (noSsr && matchMedia) {
			return () => matchMedia(query).matches;
		}
		if (ssrMatchMedia !== null) {
			const { matches } = ssrMatchMedia(query);
			return () => matches;
		}
		return getDefaultSnapshot;
	}, [getDefaultSnapshot, query, ssrMatchMedia, noSsr, matchMedia]);

	const [getSnapshot, subscribe] = useMemo(() => {
		if (matchMedia === null) {
			return [getDefaultSnapshot, () => () => {}] as const;
		}

		const mediaQueryList = matchMedia(query);
		return [
			() => mediaQueryList.matches,
			(notify: () => void) => {
				mediaQueryList.addEventListener("change", notify);
				return () => {
					mediaQueryList.removeEventListener("change", notify);
				};
			},
		] as const;
	}, [getDefaultSnapshot, matchMedia, query]);

	return maybeReactUseSyncExternalStore?.(
		subscribe,
		getSnapshot,
		getServerSnapshot,
	);
}

export const useMediaQuery = (
	queryInput: string,
	options: UseMediaQueryOptions = {},
) => {
	const supportMatchMedia =
		typeof window !== "undefined" && typeof window.matchMedia !== "undefined";
	const {
		defaultMatches = false,
		matchMedia = supportMatchMedia ? window.matchMedia : null,
		ssrMatchMedia = null,
		noSsr = false,
	} = options;

	const query = queryInput.replace(/^@media( ?)/m, "");

	if (import.meta.env.DEV && query.includes("print")) {
		console.warn(
			[
				"MUI: You have provided a `print` query to the `useMediaQuery` hook.",
				"Using the print media query to modify print styles can lead to unexpected results.",
			].join("\n"),
		);
	}

	const useMediaQueryImplementation =
		maybeReactUseSyncExternalStore !== undefined
			? useMediaQueryNew
			: useMediaQueryOld;
	const match = useMediaQueryImplementation(
		query,
		defaultMatches,
		matchMedia,
		ssrMatchMedia,
		noSsr,
	);

	useDebugValue(import.meta.env.DEV ? { query, match } : undefined);

	return match;
};
